import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { selectAllUsers } from './usersSlice';
import { useLanguage } from '../../context/LanguageContext';
import './Partners.css';

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';

function Partners() {
  const { language } = useLanguage();

  const users = useSelector(selectAllUsers);

  const sponsors = users.filter(
    (user) => user.status_id === 6
  );

  const [formData, setFormData] = useState({
    name: '',
    organisation: '',
    email: '',
    country: '',
    partnershipArea: '',
    subject: '',
    message: '',
    consent: false,
  });

  const [formStatus, setFormStatus] = useState({
    type: '',
    message: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (event) => {
    const {
      name,
      value,
      type,
      checked,
    } = event.target;

    setFormData((previousData) => ({
      ...previousData,
      [name]:
        type === 'checkbox'
          ? checked
          : value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!formData.consent) {
      setFormStatus({
        type: 'error',
        message:
          language === 'fr'
            ? "Veuillez confirmer qu'ACE peut utiliser les informations fournies afin de répondre à votre demande."
            : 'Please confirm that ACE may use the information provided to respond to your enquiry.',
      });

      return;
    }

    setIsSubmitting(true);

    setFormStatus({
      type: '',
      message: '',
    });

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/v1/partnership_enquiries`,
        {
          method: 'POST',

          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },

          body: JSON.stringify({
            partnership_enquiry: {
              name: formData.name,
              organisation: formData.organisation,
              email: formData.email,
              country: formData.country,
              partnership_area:
                formData.partnershipArea,
              subject: formData.subject,
              message: formData.message,
            },
          }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        const errorMessage =
          data.errors &&
          data.errors.length > 0
            ? data.errors.join(' ')
            : data.message ||
              (
                language === 'fr'
                  ? "Votre demande de partenariat n'a pas pu être envoyée."
                  : 'Your enquiry could not be submitted.'
              );

        throw new Error(errorMessage);
      }

      setFormStatus({
        type: 'success',
        message:
          language === 'fr'
            ? 'Merci. Votre demande de partenariat a bien été reçue.'
            : (
                data.message ||
                'Thank you. Your partnership enquiry has been received.'
              ),
      });

      setFormData({
        name: '',
        organisation: '',
        email: '',
        country: '',
        partnershipArea: '',
        subject: '',
        message: '',
        consent: false,
      });
    } catch (error) {
      setFormStatus({
        type: 'error',
        message:
          error.message ||
          (
            language === 'fr'
              ? "Une erreur s'est produite lors de l'envoi de votre demande."
              : 'Something went wrong while submitting your enquiry.'
          ),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main className="partners-page">

      {/* =====================================================
          INTRODUCTION
          ===================================================== */}

      <section className="partners-hero">
        <div className="partners-container">

          <span className="partners-eyebrow">
            {language === 'fr'
              ? 'Partenariats'
              : 'Partnerships'}
          </span>

          <h1>
            {language === 'fr'
              ? 'Devenir partenaire d’ACE'
              : 'Partner with ACE'}
          </h1>

          <div className="partners-heading-line" />

          <p className="partners-intro">
            {language === 'fr'
              ? "ACE collabore avec des partenaires de développement, des institutions publiques, des acteurs du secteur privé, des établissements de formation, des universités, des organismes de recherche et d'autres partenaires engagés en faveur du développement des entreprises, des compétences et des opportunités économiques."
              : 'ACE collaborates with development partners, public institutions, private-sector actors, training institutions, universities, research organisations and other partners committed to enterprise development, skills and economic opportunity.'}
          </p>

          <p className="partners-intro partners-intro-secondary">
            {language === 'fr'
              ? "Nous accueillons les partenariats qui associent ressources, expertise, innovation et réseaux afin de créer des opportunités durables pour les entreprises, les apprenants et les communautés."
              : 'We welcome partnerships that combine resources, expertise, innovation and networks to create sustainable opportunities for enterprises, learners and communities.'}
          </p>

        </div>
      </section>


      {/* =====================================================
          COLLABORATION AREAS
          ===================================================== */}

      <section className="partners-collaboration">
        <div className="partners-container">

          <div className="partners-section-heading">
            <span>
              {language === 'fr'
                ? 'Collaborer avec nous'
                : 'Work with us'}
            </span>

            <h2>
              {language === 'fr'
                ? 'Comment pouvons-nous collaborer ?'
                : 'How we can collaborate'}
            </h2>

            <p>
              {language === 'fr'
                ? "ACE accueille différentes formes de collaboration selon l'expertise, les priorités et les domaines d'intervention de ses partenaires."
                : 'ACE welcomes different forms of collaboration depending on partners’ expertise, priorities and areas of intervention.'}
            </p>
          </div>


          <div className="partners-cards">

            <article className="partners-card">
              <div className="partners-card-number">
                01
              </div>

              <h3>
                {language === 'fr'
                  ? 'Partenariats de programme et de financement'
                  : 'Programme & Funding Partnerships'}
              </h3>

              <p>
                {language === 'fr'
                  ? "Programmes conjoints, subventions, cofinancements et initiatives soutenant le développement des entreprises, l'emploi et une croissance économique inclusive."
                  : 'Joint programmes, grants, co-financing and initiatives supporting enterprise development, employment and inclusive economic growth.'}
              </p>
            </article>


            <article className="partners-card">
              <div className="partners-card-number">
                02
              </div>

              <h3>
                {language === 'fr'
                  ? 'Partenariats techniques'
                  : 'Technical Partnerships'}
              </h3>

              <p>
                {language === 'fr'
                  ? "Expertise technique, solutions numériques, innovation, services d'appui aux entreprises et renforcement des capacités institutionnelles."
                  : 'Technical expertise, digital solutions, innovation, business support services and institutional capacity development.'}
              </p>
            </article>


            <article className="partners-card">
              <div className="partners-card-number">
                03
              </div>

              <h3>
                {language === 'fr'
                  ? 'Formation et compétences'
                  : 'Training & Skills'}
              </h3>

              <p>
                {language === 'fr'
                  ? "Collaboration avec les centres de formation, les employeurs et les institutions afin de renforcer les compétences adaptées aux besoins du marché et les passerelles vers l'emploi."
                  : 'Collaboration with training centres, employers and institutions to strengthen market-relevant skills and employment pathways.'}
              </p>
            </article>


            <article className="partners-card">
              <div className="partners-card-number">
                04
              </div>

              <h3>
                {language === 'fr'
                  ? 'Recherche et connaissances'
                  : 'Research & Knowledge'}
              </h3>

              <p>
                {language === 'fr'
                  ? "Recherche appliquée, données, produits de connaissance, partenariats d'apprentissage et production de données probantes pour améliorer les programmes et les politiques."
                  : 'Applied research, data, knowledge products, learning partnerships and evidence to improve programmes and policy.'}
              </p>
            </article>

          </div>

        </div>
      </section>


      {/* =====================================================
          CONTACT FORM
          ===================================================== */}

      <section className="partners-contact">

        <div className="partners-container partners-contact-layout">

          <div className="partners-contact-copy">

            <span className="partners-eyebrow">
              {language === 'fr'
                ? 'Contactez-nous'
                : 'Get in touch'}
            </span>

            <h2>
              {language === 'fr'
                ? 'Explorons un partenariat'
                : "Let's explore a partnership"}
            </h2>

            <div className="partners-heading-line partners-heading-line-left" />

            <p>
              {language === 'fr'
                ? "Présentez-nous brièvement votre organisation et le type de collaboration que vous souhaitez explorer."
                : 'Tell us a little about your organisation and the type of collaboration you would like to explore.'}
            </p>

            <p>
              {language === 'fr'
                ? "Votre demande permettra à ACE d'identifier les opportunités les plus pertinentes pour engager une discussion et envisager une collaboration."
                : 'Your enquiry will help ACE identify the most relevant opportunities for discussion and collaboration.'}
            </p>

            <div className="partners-contact-note">
              <strong>
                {language === 'fr'
                  ? 'Demandes de partenariat'
                  : 'Partnership enquiries'}
              </strong>

              <span>
                {language === 'fr'
                  ? 'Développement de programmes • Financement • Coopération technique • Recherche • Formation • Collaboration avec le secteur privé'
                  : 'Programme development • Funding • Technical cooperation • Research • Training • Private-sector collaboration'}
              </span>
            </div>

          </div>


          <div className="partners-form-wrapper">

            <form
              className="partners-form"
              onSubmit={handleSubmit}
            >

              <div className="partners-form-heading">
                <h3>
                  {language === 'fr'
                    ? 'Demande de partenariat'
                    : 'Partnership Enquiry'}
                </h3>

                <p>
                  {language === 'fr'
                    ? 'Les champs marqués d’un * sont obligatoires.'
                    : 'Fields marked with * are required.'}
                </p>
              </div>


              <div className="partners-form-grid">

                <div className="partners-form-group">
                  <label htmlFor="name">
                    {language === 'fr'
                      ? 'Nom complet *'
                      : 'Full name *'}
                  </label>

                  <input
                    id="name"
                    name="name"
                    type="text"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder={
                      language === 'fr'
                        ? 'Votre nom complet'
                        : 'Your full name'
                    }
                    required
                  />
                </div>


                <div className="partners-form-group">
                  <label htmlFor="organisation">
                    {language === 'fr'
                      ? 'Organisation *'
                      : 'Organisation *'}
                  </label>

                  <input
                    id="organisation"
                    name="organisation"
                    type="text"
                    value={formData.organisation}
                    onChange={handleChange}
                    placeholder={
                      language === 'fr'
                        ? "Nom de l'organisation"
                        : 'Organisation name'
                    }
                    required
                  />
                </div>


                <div className="partners-form-group">
                  <label htmlFor="email">
                    {language === 'fr'
                      ? 'Adresse e-mail *'
                      : 'Email address *'}
                  </label>

                  <input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="name@organisation.org"
                    required
                  />
                </div>


                <div className="partners-form-group">
                  <label htmlFor="country">
                    {language === 'fr'
                      ? 'Pays'
                      : 'Country'}
                  </label>

                  <input
                    id="country"
                    name="country"
                    type="text"
                    value={formData.country}
                    onChange={handleChange}
                    placeholder={
                      language === 'fr'
                        ? 'Pays'
                        : 'Country'
                    }
                  />
                </div>


                <div className="partners-form-group partners-form-full">
                  <label htmlFor="partnershipArea">
                    {language === 'fr'
                      ? 'Domaine de partenariat *'
                      : 'Partnership area *'}
                  </label>

                  <select
                    id="partnershipArea"
                    name="partnershipArea"
                    value={formData.partnershipArea}
                    onChange={handleChange}
                    required
                  >
                    <option value="">
                      {language === 'fr'
                        ? 'Sélectionnez un domaine de partenariat'
                        : 'Select a partnership area'}
                    </option>

                    <option value="programme-funding">
                      {language === 'fr'
                        ? 'Partenariat de programme / financement'
                        : 'Programme / funding partnership'}
                    </option>

                    <option value="technical">
                      {language === 'fr'
                        ? 'Partenariat technique'
                        : 'Technical partnership'}
                    </option>

                    <option value="research">
                      {language === 'fr'
                        ? 'Collaboration de recherche / académique'
                        : 'Research / academic collaboration'}
                    </option>

                    <option value="training">
                      {language === 'fr'
                        ? 'Partenariat de formation et de développement des compétences'
                        : 'Training and skills partnership'}
                    </option>

                    <option value="private-sector">
                      {language === 'fr'
                        ? 'Collaboration avec le secteur privé'
                        : 'Private-sector collaboration'}
                    </option>

                    <option value="institutional">
                      {language === 'fr'
                        ? 'Partenariat institutionnel'
                        : 'Institutional partnership'}
                    </option>

                    <option value="other">
                      {language === 'fr'
                        ? 'Autre'
                        : 'Other'}
                    </option>
                  </select>
                </div>


                <div className="partners-form-group partners-form-full">
                  <label htmlFor="subject">
                    {language === 'fr'
                      ? 'Objet *'
                      : 'Subject *'}
                  </label>

                  <input
                    id="subject"
                    name="subject"
                    type="text"
                    value={formData.subject}
                    onChange={handleChange}
                    placeholder={
                      language === 'fr'
                        ? 'De quoi souhaitez-vous discuter ?'
                        : 'What would you like to discuss?'
                    }
                    required
                  />
                </div>


                <div className="partners-form-group partners-form-full">
                  <label htmlFor="message">
                    {language === 'fr'
                      ? 'Message *'
                      : 'Message *'}
                  </label>

                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows="7"
                    placeholder={
                      language === 'fr'
                        ? "Présentez-nous votre organisation, votre idée et la manière dont vous souhaiteriez collaborer avec ACE."
                        : 'Tell us about your organisation, your idea and how you would like to collaborate with ACE.'
                    }
                    required
                  />
                </div>

              </div>


              <label className="partners-consent">

                <input
                  type="checkbox"
                  name="consent"
                  checked={formData.consent}
                  onChange={handleChange}
                  required
                />

                <span>
                  {language === 'fr'
                    ? "J'autorise ACE à utiliser les informations fournies dans ce formulaire afin de répondre à ma demande de partenariat. *"
                    : 'I consent to ACE using the information provided in this form to respond to my partnership enquiry. *'}
                </span>

              </label>


              <button
                type="submit"
                className="partners-submit"
                disabled={isSubmitting}
              >

                <span>
                  {isSubmitting
                    ? (
                        language === 'fr'
                          ? 'Envoi en cours...'
                          : 'Sending...'
                      )
                    : (
                        language === 'fr'
                          ? 'Envoyer la demande de partenariat'
                          : 'Send partnership enquiry'
                      )}
                </span>

                {!isSubmitting && (
                  <span aria-hidden="true">
                    →
                  </span>
                )}

              </button>


              {formStatus.message && (
                <div
                  className={`partners-form-status ${
                    formStatus.type === 'success'
                      ? 'partners-form-status-success'
                      : 'partners-form-status-error'
                  }`}
                  role="status"
                >
                  {formStatus.message}
                </div>
              )}

            </form>

          </div>

        </div>

      </section>


      {/* =====================================================
          EXISTING PARTNERS
          ===================================================== */}

      {sponsors.length > 0 && (
        <section className="partners-existing">

          <div className="partners-container">

            <div className="partners-section-heading">
              <span>
                {language === 'fr'
                  ? 'Notre réseau'
                  : 'Our network'}
              </span>

              <h2>
                {language === 'fr'
                  ? 'Partenaires'
                  : 'Partners'}
              </h2>
            </div>


            <div className="partners-logos">

              {sponsors.map((partner) => (
                <div
                  className="partners-logo-card"
                  key={`partner-${partner.id}`}
                >

                  {partner.mainimageurl && (
                    <img
                      src={`${process.env.PUBLIC_URL}/${partner.mainimageurl}`}
                      alt={
                        partner.name ||
                        (
                          language === 'fr'
                            ? 'Partenaire ACE'
                            : 'ACE partner'
                        )
                      }
                    />
                  )}

                </div>
              ))}

            </div>

          </div>

        </section>
      )}

    </main>
  );
}

export default Partners;
